/**
 * Workspace page showing the code-server iframe
 */

import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  WorkspaceStatusBadge,
  SessionCountdown,
  ExpiryBanner,
  useWorkspace,
  useDeleteWorkspace,
  useTerminalTabs,
  ViewModeToggle,
  BrowserView,
  TerminalTabs,
  type WorkspaceViewMode,
} from '@features/workspace';
import { LoginStatusPills } from '@features/logins/components/LoginStatusPill';
import { Toolbar } from '@components/ui/Toolbar';
import { ROUTES, STORAGE_KEYS } from '@utils/constants';
import { ensureFreshAccessToken } from '@services/tokenRefresh';

export function WorkspacePage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: workspace, isLoading, error } = useWorkspace(id ?? '');
  const { mutateAsync: deleteWorkspace, isPending: isDeleting } = useDeleteWorkspace();

  const [storedViewMode, setViewMode] = useState<WorkspaceViewMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.WORKSPACE_VIEW_MODE);
    return stored === 'terminal' || stored === 'browser' ? stored : 'ide';
  });

  // A stored 'browser' view falls back to the IDE when the workspace lacks the feature.
  const hasBrowser = workspace?.features.includes('browser') ?? false;
  const viewMode: WorkspaceViewMode =
    storedViewMode === 'browser' && !hasBrowser ? 'ide' : storedViewMode;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.WORKSPACE_VIEW_MODE, viewMode);
  }, [viewMode]);

  const terminalActive = viewMode === 'terminal' && workspace?.status === 'running';
  const tabs = useTerminalTabs(id ?? '', terminalActive);

  // Keep the session alive while the workspace is open, and push the current
  // token into the iframe's ws_auth cookie (only set on initial load).
  // ensureFreshAccessToken() renews the stored token when it nears expiry.
  // A user working inside the terminal/IDE iframe makes no API calls, so the
  // 401 interceptor alone never renewed it: the token (session TTL 1h) and the
  // IdP session behind the refresh token idled out mid-work, and the next
  // foreground call bounced to /login. It never redirects on failure itself.
  useEffect(() => {
    if (!workspace?.url || workspace.status !== 'running' || !id) return;

    const REFRESH_INTERVAL_MS = 3 * 60 * 1000;

    const refreshCookie = async (): Promise<void> => {
      const token = await ensureFreshAccessToken();
      if (!token) return;

      await fetch(`/workspace/${id}/refresh-auth?token=${encodeURIComponent(token)}`, {
        credentials: 'same-origin',
      }).catch(() => {
        // Silent failure — next interval will retry.
      });
    };

    void refreshCookie(); // Immediate refresh on mount in case cookie is already stale
    const intervalId = setInterval(() => { void refreshCookie(); }, REFRESH_INTERVAL_MS);
    return () => { clearInterval(intervalId); };
  }, [workspace?.url, workspace?.status, id]);

  // Build the iframe URL, sampling the access token only when the workspace URL
  // or view mode changes — NOT on every poll render. The refresh interceptor
  // rotates the token in localStorage; if src tracked that, the iframe would
  // reload mid-session and ttyd would raise its beforeunload ("leave site?")
  // prompt. The token in `?token=` is only consumed on initial load to set the
  // ws_auth cookie, which the refreshCookie effect above keeps alive thereafter.
  const iframeSrc = useMemo(() => {
    if (!workspace?.url) return '';
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ?? '';
    return viewMode === 'terminal'
      ? `${workspace.url}/terminal/?token=${token}`
      : `${workspace.url}?token=${token}`;
  }, [workspace?.url, viewMode]);

  const handleDelete = async (): Promise<void> => {
    if (!id) return;
    try {
      await deleteWorkspace(id);
      void navigate(ROUTES.HOME);
    } catch (err) {
      console.error('Failed to delete workspace:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="workspace-page">
        <div className="workspace-page__centered">
          <div className="workspace-page__message">
            <div className="workspace-page__spinner" />
            <p className="workspace-page__subtitle">Loading workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workspace-page">
        <div className="workspace-page__centered">
          <div className="workspace-page__message">
            <div className="workspace-page__error-icon">!</div>
            <h2 className="workspace-page__title">Failed to load workspace</h2>
            <p className="workspace-page__subtitle">{error.message}</p>
            <button
              type="button"
              onClick={() => { void navigate(ROUTES.HOME); }}
              className="workspace-page__link"
            >
              Back to Workspaces
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="workspace-page">
        <div className="workspace-page__centered">
          <div className="workspace-page__message">
            <h2 className="workspace-page__title">Workspace not found</h2>
            <button
              type="button"
              onClick={() => { void navigate(ROUTES.HOME); }}
              className="workspace-page__link"
            >
              Back to Workspaces
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Building state
  if (workspace.status === 'building') {
    return (
      <div className="workspace-page">
        <div className="workspace-page__centered">
          <div className="workspace-page__message">
            <div className="workspace-page__spinner" />
            <h2 className="workspace-page__title">Building your workspace environment...</h2>
            <p className="workspace-page__subtitle">
              This may take a few minutes for first-time feature combinations
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Starting state
  if (workspace.status === 'starting') {
    return (
      <div className="workspace-page">
        <div className="workspace-page__centered">
          <div className="workspace-page__message">
            <div className="workspace-page__spinner" />
            <h2 className="workspace-page__title">Starting Workspace</h2>
            <p className="workspace-page__subtitle">
              {workspace.project_name} ({workspace.branch})
            </p>
            <p className="workspace-page__hint">This may take a few moments...</p>
          </div>
        </div>
      </div>
    );
  }

  // Terminated or error state
  if (workspace.status === 'terminated' || workspace.status === 'error') {
    return (
      <div className="workspace-page">
        <div className="workspace-page__centered">
          <div className="workspace-page__message">
            <WorkspaceStatusBadge status={workspace.status} />
            <h2 className="workspace-page__title" style={{ marginTop: '1rem' }}>
              {workspace.status === 'error'
                ? 'Workspace Error'
                : workspace.termination_reason === 'expired'
                  ? 'Session Expired' : 'Workspace Terminated'}
            </h2>
            <p className="workspace-page__subtitle">
              {workspace.project_name} ({workspace.branch})
            </p>
            {workspace.termination_reason === 'expired' && (
              <p className="workspace-page__hint">This workspace reached its session time limit.</p>
            )}
            {workspace.error_message != null && (
              <p className="workspace-page__error-detail">{workspace.error_message}</p>
            )}
            <button
              type="button"
              onClick={() => { void navigate(ROUTES.HOME); }}
              className="workspace-page__button"
            >
              Launch New Workspace
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Running state - show iframe (iframeSrc computed above via useMemo)
  return (
    <div className="workspace-page">
      <Toolbar
        info={<>
          <WorkspaceStatusBadge status={workspace.status} />
          <LoginStatusPills compact />
          <span className="app-toolbar__project">{workspace.project_name}</span>
          <span className="app-toolbar__branch">({workspace.branch})</span>
        </>}
        actions={<>
          <SessionCountdown workspaceId={id ?? ''} expiresAt={workspace.expires_at} />
          <ViewModeToggle
            value={viewMode}
            onChange={setViewMode}
            disabled={false}
            showBrowser={hasBrowser}
          />
          <button
            type="button"
            onClick={() => { void handleDelete(); }}
            disabled={isDeleting}
            className="app-toolbar__danger"
          >
            {isDeleting ? 'Stopping...' : 'Stop'}
          </button>
        </>}
      />

      <ExpiryBanner expiresAt={workspace.expires_at} />

      {viewMode === 'terminal' && tabs.windows.length > 0 && (
        <TerminalTabs
          windows={tabs.windows}
          onSelect={tabs.selectWindow}
          onNew={tabs.newWindow}
          onClose={tabs.killWindow}
          disabled={tabs.isMutating}
        />
      )}

      {workspace.url && viewMode === 'browser' ? (
        <BrowserView workspaceId={id ?? ''} workspaceUrl={workspace.url} />
      ) : workspace.url ? (
        <iframe
          key={viewMode}
          src={iframeSrc}
          title={viewMode === 'terminal' ? 'Workspace terminal' : 'Workspace IDE'}
          className="workspace-page__iframe"
          allow="clipboard-read *; clipboard-write *"
        />
      ) : (
        <div className="workspace-page__centered">
          <p className="workspace-page__subtitle">Waiting for workspace URL...</p>
        </div>
      )}
    </div>
  );
}
